import React, { useState, useEffect, useRef, useCallback, useContext, useMemo } from 'react';
import { format } from 'date-fns';
import {
  Mail,
  MessageSquare,
  Smartphone,
  Bell,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Search,
  Filter,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Eye,
  RefreshCw,
  ShieldAlert,
  Pause,
  Play,
  Zap,
} from 'lucide-react';
import AdminHeader from '../../components/AdminHeader';
import { apiClient, AuthContext } from '../../App';
import {
  appendNotificationAuditFilters,
  getPushOutcomeLabel,
  getReadablePushReason,
} from '../../utils/notificationAudit';

// Status badge colors
const STATUS_STYLES = {
  delivered: 'bg-green-100 text-green-800 border border-green-200',
  sent: 'bg-blue-100 text-blue-800 border border-blue-200',
  provider_accepted: 'bg-indigo-100 text-indigo-800 border border-indigo-200',
  queued: 'bg-gray-100 text-gray-600 border border-gray-200',
  pending: 'bg-gray-100 text-gray-500 border border-gray-200',
  skipped: 'bg-gray-100 text-gray-700 border border-gray-300',
  failed: 'bg-red-100 text-red-800 border border-red-200',
  undelivered: 'bg-orange-100 text-orange-800 border border-orange-200',
};

import { parseUTC } from '../../utils/datetime';

// Format time in user's local timezone with abbreviation
const formatLocalTime = (timestamp) => {
  if (!timestamp) return '-';
  const date = parseUTC(timestamp);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZoneName: 'short'
  });
};

// Channel icons
const ChannelIcon = ({ channel }) => {
  switch (channel) {
    case 'sms':
      return <Smartphone className="w-4 h-4" />;
    case 'email':
      return <Mail className="w-4 h-4" />;
    case 'in_app':
      return <MessageSquare className="w-4 h-4" />;
    case 'push':
      return <Bell className="w-4 h-4" />;
    default:
      return null;
  }
};

// Status icon
const StatusIcon = ({ status }) => {
  switch (status) {
    case 'delivered':
      return <CheckCircle className="w-4 h-4 text-green-600" />;
    case 'sent':
      return <CheckCircle className="w-4 h-4 text-blue-600" />;
    case 'provider_accepted':
      return <CheckCircle className="w-4 h-4 text-indigo-600" />;
    case 'queued':
    case 'pending':
      return <Clock className="w-4 h-4 text-yellow-600" />;
    case 'failed':
      return <XCircle className="w-4 h-4 text-red-600" />;
    case 'skipped':
      return <AlertTriangle className="w-4 h-4 text-gray-600" />;
    case 'undelivered':
      return <AlertTriangle className="w-4 h-4 text-orange-600" />;
    default:
      return <AlertTriangle className="w-4 h-4 text-gray-600" />;
  }
};

// Message Detail Modal
const MessageDetailModal = ({ delivery, onClose }) => {
  if (!delivery) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <h2 className="text-xl font-semibold" data-testid="detail-modal-title">Notification Details</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              data-testid="detail-modal-close"
            >
              x
            </button>
          </div>

          {/* Status Banner */}
          <div className={`p-4 rounded-lg mb-6 ${STATUS_STYLES[delivery.status] || 'bg-gray-100'}`}>
            <div className="flex items-center gap-2">
              <StatusIcon status={delivery.status} />
              <span className="font-medium capitalize">{delivery.status}</span>
              {delivery.provider_status && (
                <span className="text-sm">
                  (Provider: {delivery.provider_status})
                </span>
              )}
            </div>
            {delivery.suppression_reason && (
              <p className="mt-2 text-sm">
                <strong>Blocked:</strong> {delivery.suppression_reason}
              </p>
            )}
            {delivery.provider_error_message && (
              <p className="mt-2 text-sm">
                <strong>Error:</strong> {delivery.provider_error_message}
                {delivery.provider_error_code && ` (Code: ${delivery.provider_error_code})`}
              </p>
            )}
          </div>

          {/* Timeline */}
          <div className="mb-6">
            <h3 className="font-medium mb-3">Delivery Timeline</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Created</span>
                <span>{delivery.created_at ? formatLocalTime(delivery.created_at) : '-'}</span>
              </div>
              {delivery.queued_at && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Queued</span>
                  <span>{formatLocalTime(delivery.queued_at)}</span>
                </div>
              )}
              {delivery.sent_at && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Sent to carrier</span>
                  <span>{formatLocalTime(delivery.sent_at)}</span>
                </div>
              )}
              {delivery.delivered_at && (
                <div className="flex justify-between text-green-700">
                  <span>Delivered to device</span>
                  <span>{formatLocalTime(delivery.delivered_at)}</span>
                </div>
              )}
              {delivery.failed_at && (
                <div className="flex justify-between text-red-600">
                  <span>Failed</span>
                  <span>{formatLocalTime(delivery.failed_at)}</span>
                </div>
              )}
              {delivery.provider_updated_at && (
                <div className="flex justify-between text-blue-600">
                  <span>Last Provider Update</span>
                  <span>{formatLocalTime(delivery.provider_updated_at)}</span>
                </div>
              )}
            </div>
          </div>

          {delivery.channel === 'push' && delivery.push_audit && (
            <div className="mb-6" data-testid="push-delivery-trace">
              <h3 className="font-medium mb-3">Push Delivery Trace</h3>
              <div className="bg-indigo-50 border border-indigo-100 p-4 rounded-lg text-sm space-y-2">
                <p>
                  <strong>Outcome:</strong>{' '}
                  {delivery.status === 'provider_accepted'
                    ? 'APNs provider accepted'
                    : delivery.status}
                </p>
                <p>
                  <strong>First failed stage:</strong>{' '}
                  {delivery.push_audit.failure_stage || 'None recorded'}
                </p>
                <p>
                  <strong>Failure reason:</strong>{' '}
                  {delivery.push_audit.failure_reason || 'None'}
                </p>
                <p><strong>Environment:</strong> {delivery.push_audit.environment || '-'}</p>
                <p><strong>Provider selected:</strong> {delivery.push_audit.provider_selected || '-'}</p>
                <p>
                  <strong>Provider attempted:</strong>{' '}
                  {delivery.push_audit.provider_attempted ? 'Yes' : 'No'}
                </p>
                <p>
                  <strong>APNs response:</strong>{' '}
                  {delivery.push_audit.apns_response || 'No APNs response'}
                </p>
                <p className="text-indigo-800">
                  APNs acceptance confirms backend handoff only. It does not
                  prove that iOS displayed a banner.
                </p>
              </div>
            </div>
          )}
          {delivery.channel === 'push' && delivery.status === 'sent' && !delivery.push_audit && (
            <div className="mb-6 bg-amber-50 border border-amber-200 p-4 rounded-lg text-sm">
              <strong>Legacy push record:</strong> Provider outcome not recorded.
              The stored sent status has not been changed.
            </div>
          )}

          {/* Recipient */}
          <div className="mb-6">
            <h3 className="font-medium mb-3">Recipient</h3>
            <div className="bg-gray-50 p-4 rounded-lg text-sm">
              <div className="flex items-center gap-2 mb-2">
                <ChannelIcon channel={delivery.channel} />
                <span className="capitalize font-medium">{delivery.channel}</span>
              </div>
              {delivery.recipient_username && (
                <p>
                  <strong>User:</strong>{' '}
                  <a
                    href={`/player/${delivery.recipient_username}`}
                    className="text-blue-600 hover:underline"
                  >
                    {delivery.recipient_username}
                  </a>
                </p>
              )}
              {delivery.recipient_email && (
                <p><strong>Email:</strong> {delivery.recipient_email}</p>
              )}
              {delivery.recipient_phone && (
                <p><strong>Phone:</strong> {delivery.recipient_phone}</p>
              )}
            </div>
          </div>

          {/* Message Content */}
          <div className="mb-6">
            <h3 className="font-medium mb-3">Message Content</h3>
            <div className="bg-gray-50 p-4 rounded-lg">
              {delivery.subject && (
                <p className="font-medium mb-2">{delivery.subject}</p>
              )}
              <p className="text-sm whitespace-pre-wrap">{delivery.body}</p>
            </div>
          </div>

          {/* Template Metadata */}
          {delivery.template_metadata && Object.keys(delivery.template_metadata).length > 0 && (
            <div className="mb-6">
              <h3 className="font-medium mb-3">Template Inputs</h3>
              <div className="bg-gray-50 p-4 rounded-lg text-sm">
                {Object.entries(delivery.template_metadata).map(([key, value]) => (
                  <div key={key} className="flex justify-between py-1">
                    <span className="text-gray-500">{key}</span>
                    <span className="font-mono">
                      {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Provider Details */}
          <div className="mb-6">
            <h3 className="font-medium mb-3">Provider Details</h3>
            <div className="bg-gray-50 p-4 rounded-lg text-sm space-y-1">
              <p><strong>Provider:</strong> {delivery.provider || '-'}</p>
              <p><strong>Message ID:</strong> <span className="font-mono text-xs">{delivery.provider_message_id || '-'}</span></p>
              <p><strong>Provider Status:</strong> {delivery.provider_status || '-'}</p>
              <p><strong>Template:</strong> {delivery.template_used || '-'}</p>
              {delivery.provider_error_code && (
                <p className="text-red-600"><strong>Error Code:</strong> {delivery.provider_error_code}</p>
              )}
            </div>
          </div>

          {/* Context Links */}
          <div className="flex gap-4">
            {delivery.game_id && (
              <a
                href={`/games/${delivery.game_id}`}
                className="text-blue-600 hover:underline text-sm flex items-center gap-1"
              >
                <ExternalLink className="w-3 h-3" />
                View Game
              </a>
            )}
            <span className="text-gray-400 text-sm">
              Event: {delivery.event_type} | ID: {delivery.event_id?.slice(0, 8)}...
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Audit Log Component
export default function NotificationAuditLog() {
  const { user } = useContext(AuthContext) || {};
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const exportPending = useRef(false);
  const exportCsv = async () => {
    if (exportPending.current) return;
    exportPending.current = true;
    setExporting(true);
    setExportError(null);
    let url;
    let link;
    try {
      const params = appendNotificationAuditFilters(new URLSearchParams(), effectiveExportFilters);
      const response = await apiClient.get(`/admin/notifications/audit/export.csv?${params}`, { responseType: 'blob' });
      url = URL.createObjectURL(response.data);
      link = document.createElement('a');
      link.href = url;
      link.download = 'notification-audit.csv';
      document.body.appendChild(link);
      link.click();
    } catch (err) {
      const status = err.response?.status;
      setExportError(status === 401 ? 'Sign in again to export the audit log.'
        : status === 403 ? 'Administrator access is required to export.'
        : status === 429 ? 'Export limit reached. Wait a minute before retrying.'
        : status === 413 ? 'Too many rows or too much data. Narrow the filters and retry.'
        : 'CSV export failed. Please retry.');
    } finally {
      link?.remove();
      if (url) URL.revokeObjectURL(url);
      exportPending.current = false;
      setExporting(false);
    }
  };
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Filters
  const [filters, setFilters] = useState({
    channel: '',
    status: '',
    event_type: '',
    recipient: '',
    game_id: '',
    start_date: '',
    end_date: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  // Keep the displayed bounds and request bounds identical until filters change.
  const effectiveExportFilters = useMemo(() => {
    if (filters.start_date || filters.end_date) return filters;
    const end = new Date();
    return { ...filters,
      start_date: new Date(end.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString(),
      end_date: end.toISOString(),
    };
  }, [filters]);
  const exportDateLabel = value => value ? new Date(value).toISOString() : 'Unbounded';
  
  // Detail modal
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  
  // Stats
  const [stats, setStats] = useState(null);
  
  // Event types for filter
  const [eventTypes, setEventTypes] = useState([]);
  
  // Safe mode status
  const [safeMode, setSafeMode] = useState(null);

  // Auto-refresh state
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(10);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const autoRefreshRef = useRef(null);

  // Test SMS
  const [testSmsLoading, setTestSmsLoading] = useState(false);
  const [testSmsResult, setTestSmsResult] = useState(null);
  const [testPushLoading, setTestPushLoading] = useState(false);
  const [testPushResult, setTestPushResult] = useState(null);

  const sendTestSms = async () => {
    setTestSmsLoading(true);
    setTestSmsResult(null);
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const response = await apiClient.post(`/admin/notifications/test-sms?tz=${encodeURIComponent(tz)}`);
      setTestSmsResult(response.data);
      // Auto-enable live refresh to watch status
      if (!autoRefresh) {
        setAutoRefresh(true);
        setRefreshInterval(5);
      }
      // Refresh immediately
      setTimeout(() => { fetchAuditLog(); fetchStats(); }, 1000);
    } catch (err) {
      setTestSmsResult({
        success: false,
        error: err.response?.data?.detail || err.message || 'Failed to send test SMS'
      });
    } finally {
      setTestSmsLoading(false);
    }
  };

  const sendTestPush = async () => {
    setTestPushLoading(true);
    setTestPushResult(null);
    try {
      const response = await apiClient.post('/admin/notifications/test-push');
      setTestPushResult(response.data);
      setTimeout(() => { fetchAuditLog(); fetchStats(); }, 500);
    } catch (err) {
      setTestPushResult({
        status: 'failed',
        failure_reason: err.response?.data?.detail || err.message || 'Push diagnostic failed',
      });
    } finally {
      setTestPushLoading(false);
    }
  };

  // Fetch safe mode status
  const fetchSafeMode = async () => {
    try {
      const response = await apiClient.get('/admin/notifications/safe-mode');
      setSafeMode(response.data);
    } catch (err) {
      console.error('Failed to fetch safe mode status:', err);
    }
  };

  // Fetch audit log
  const fetchAuditLog = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
      });
      
      appendNotificationAuditFilters(params, filters);
      
      const response = await apiClient.get(`/admin/notifications/audit?${params}`);
      const data = response.data;
      setEntries(data.entries);
      setTotal(data.total);
      setLastRefreshed(new Date());
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to fetch audit log');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [page, pageSize, filters]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const params = appendNotificationAuditFilters(
        new URLSearchParams({ hours: '24' }),
        filters,
      );
      const response = await apiClient.get(`/admin/notifications/stats?${params}`);
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, [filters]);

  // Fetch event types
  const fetchEventTypes = async () => {
    try {
      const response = await apiClient.get('/admin/notifications/events');
      setEventTypes(response.data.event_types);
    } catch (err) {
      console.error('Failed to fetch event types:', err);
    }
  };

  // Fetch delivery detail
  const fetchDetail = async (deliveryId) => {
    setDetailLoading(true);
    try {
      const response = await apiClient.get(`/admin/notifications/audit/${deliveryId}`);
      setSelectedDelivery(response.data);
    } catch (err) {
      console.error('Failed to fetch detail:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  // Auto-refresh logic
  useEffect(() => {
    if (autoRefresh) {
      autoRefreshRef.current = setInterval(() => {
        fetchAuditLog(true); // silent refresh
        fetchStats();
      }, refreshInterval * 1000);
    }
    return () => {
      if (autoRefreshRef.current) {
        clearInterval(autoRefreshRef.current);
        autoRefreshRef.current = null;
      }
    };
  }, [autoRefresh, refreshInterval, fetchAuditLog, fetchStats]);

  useEffect(() => {
    fetchAuditLog();
    fetchStats();
    fetchEventTypes();
    fetchSafeMode();
  }, [page, filters]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({
      channel: '',
      status: '',
      event_type: '',
      recipient: '',
      game_id: '',
      start_date: '',
      end_date: '',
    });
    setPage(1);
  };

  // Quick filter: SMS only
  const filterSmsOnly = () => {
    setFilters(prev => ({ ...prev, channel: 'sms' }));
    setShowFilters(true);
    setPage(1);
  };

  // Quick filter: Push only
  const filterPushOnly = () => {
    setFilters(prev => ({ ...prev, channel: 'push' }));
    setShowFilters(true);
    setPage(1);
  };

  const handleManualRefresh = () => {
    fetchAuditLog();
    fetchStats();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader />
      {exportError && <p role="alert" className="text-red-700 p-4">{exportError}</p>}
      {user?.role === 'admin' && <div className="text-sm text-gray-600 px-4 pt-4">
        <p data-testid="export-date-policy">Export uses the selected filters. With no date filters selected, export covers the last 30 days.</p>
        <p>For exact identifiers in Excel, use Data → From Text/CSV → Transform Data,
          remove automatic type conversion and set all columns to Text before loading.
          Opening CSV directly can round long IDs or remove leading zeros. Cells beginning
          with formula prefixes have a protective apostrophe, which may remain visible.</p>
      </div>}
      <div className="max-w-7xl mx-auto p-6">
        {/* Safe Mode Banner */}
        {safeMode?.enabled && (
          <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
            <ShieldAlert className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-amber-800">Safe Mode Enabled</h3>
              <p className="text-amber-700 text-sm mt-1">
                All notifications are being routed to test recipients to protect sender reputation.
              </p>
              <div className="mt-2 flex flex-wrap gap-4 text-sm">
                {safeMode.safe_email && (
                  <span className="bg-amber-100 px-2 py-1 rounded text-amber-800">
                    <Mail className="w-3 h-3 inline mr-1" />
                    Email: {safeMode.safe_email}
                  </span>
                )}
                {safeMode.safe_phone && (
                  <span className="bg-amber-100 px-2 py-1 rounded text-amber-800">
                    <Smartphone className="w-3 h-3 inline mr-1" />
                    SMS: {safeMode.safe_phone}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Test SMS Result Banner */}
        {testSmsResult && (
          <div className={`mb-6 rounded-lg p-4 flex items-start gap-3 border ${testSmsResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`} data-testid="test-sms-result">
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <h3 className={`font-semibold ${testSmsResult.success ? 'text-green-800' : 'text-red-800'}`}>
                  {testSmsResult.success ? 'Test SMS Sent' : 'Test SMS Failed'}
                </h3>
                <button
                  onClick={() => setTestSmsResult(null)}
                  className="text-gray-400 hover:text-gray-600 text-sm"
                  data-testid="dismiss-test-result"
                >
                  Dismiss
                </button>
              </div>
              {testSmsResult.success ? (
                <div className="mt-2 space-y-1 text-sm text-green-700">
                  <p><strong>Notification ID:</strong> <span className="font-mono text-xs" data-testid="test-notification-id">{testSmsResult.delivery_id}</span></p>
                  <p><strong>Twilio SID:</strong> <span className="font-mono text-xs" data-testid="test-twilio-sid">{testSmsResult.twilio_sid}</span></p>
                  <p><strong>Phone:</strong> {testSmsResult.phone}</p>
                  <p className="text-green-600 mt-1 flex items-center gap-1">
                    <Zap className="w-3 h-3" />
                    {testSmsResult.note}
                  </p>
                </div>
              ) : (
                <p className="mt-2 text-sm text-red-700" data-testid="test-error-message">
                  {testSmsResult.error}
                </p>
              )}
            </div>
          </div>
        )}

        {testPushResult && (
          <div
            className={`mb-6 rounded-lg p-4 border ${
              testPushResult.apns_accepted
                ? 'bg-indigo-50 border-indigo-200'
                : 'bg-amber-50 border-amber-200'
            }`}
            data-testid="test-push-result"
          >
            <div className="flex items-center justify-between gap-4">
              <h3 className="font-semibold text-gray-900">Push diagnostic</h3>
              <button
                onClick={() => setTestPushResult(null)}
                className="text-gray-500 hover:text-gray-800 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 rounded"
              >
                Dismiss
              </button>
            </div>
            <div className="mt-2 text-sm space-y-1">
              <p><strong>Status:</strong> {testPushResult.status}</p>
              <p><strong>Provider:</strong> {testPushResult.provider || 'Not selected'}</p>
              <p>
                <strong>First failed stage:</strong>{' '}
                {testPushResult.first_failed_stage || 'None'}
              </p>
              {testPushResult.failure_reason && (
                <p><strong>Reason:</strong> {testPushResult.failure_reason}</p>
              )}
              <p>{testPushResult.message}</p>
            </div>
          </div>
        )}

        {/* Header Row */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="audit-log-title">Notification Audit Log</h1>
            <p className="text-gray-600 text-sm mt-1">
              Track notification attempts across push, SMS, email, and in-app channels
            </p>
          </div>

          {/* Auto-Refresh Controls */}
          <div className="flex flex-wrap items-center gap-3" data-testid="auto-refresh-controls">
            {user?.role === 'admin' && <p data-testid="export-effective-range" className="text-sm text-gray-600">
              Export range (UTC): {exportDateLabel(effectiveExportFilters.start_date)} to {exportDateLabel(effectiveExportFilters.end_date)}
            </p>}
            {user?.role === 'admin' && (
              <button type="button" onClick={exportCsv} disabled={exporting}
                data-testid="export-audit-csv"
                className="bg-gray-800 text-white rounded-lg px-3 py-2 text-sm disabled:opacity-50">
                {exporting ? 'Exporting…' : 'Export CSV'}
              </button>
            )}
            <button
              onClick={sendTestPush}
              disabled={testPushLoading}
              className="flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg px-3 py-2 text-sm font-medium shadow-sm disabled:opacity-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
              data-testid="send-test-push-button"
            >
              <Bell className="w-4 h-4" />
              {testPushLoading ? 'Testing...' : 'Test Push'}
            </button>
            <button
              onClick={sendTestSms}
              disabled={testSmsLoading}
              className="flex items-center gap-1.5 bg-orange-500 hover:bg-orange-600 text-white rounded-lg px-3 py-2 text-sm font-medium shadow-sm disabled:opacity-50 transition-colors"
              data-testid="send-test-sms-button"
            >
              <Smartphone className="w-4 h-4" />
              {testSmsLoading ? 'Sending...' : 'Send Test SMS'}
            </button>
            <div className="flex items-center gap-2 bg-white border rounded-lg px-3 py-2 shadow-sm">
              <button
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={`flex items-center gap-1.5 text-sm font-medium ${autoRefresh ? 'text-green-600' : 'text-gray-500'}`}
                data-testid="auto-refresh-toggle"
              >
                {autoRefresh ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                {autoRefresh ? 'Live' : 'Auto-refresh'}
              </button>
              {autoRefresh && (
                <select
                  value={refreshInterval}
                  onChange={(e) => setRefreshInterval(Number(e.target.value))}
                  className="text-sm border-l pl-2 ml-1 text-gray-600"
                  data-testid="refresh-interval-select"
                >
                  <option value={5}>5s</option>
                  <option value={10}>10s</option>
                  <option value={30}>30s</option>
                </select>
              )}
            </div>
            <button
              onClick={handleManualRefresh}
              disabled={loading}
              className="flex items-center gap-1.5 bg-white border rounded-lg px-3 py-2 text-sm text-gray-600 hover:text-gray-800 shadow-sm"
              data-testid="manual-refresh-button"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
            <div className="bg-white p-4 rounded-lg shadow-sm border" data-testid="stat-total">
              <p className="text-sm text-gray-500">Total</p>
              <p className="text-2xl font-bold">{stats.total}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border" data-testid="stat-delivered">
              <p className="text-sm text-gray-500" title="Successful backend or provider handoffs">
                Delivered
              </p>
              <p className="text-2xl font-bold text-green-600">
                {stats.delivered ?? stats.by_status?.delivered ?? 0}
              </p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border" data-testid="stat-failed">
              <p className="text-sm text-gray-500">Failed</p>
              <p className="text-2xl font-bold text-red-600">
                {stats.failed ?? stats.by_status?.failed ?? 0}
              </p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border" data-testid="stat-rate">
              <p className="text-sm text-gray-500">Delivery Rate</p>
              <p className="text-2xl font-bold">
                {(stats.delivery_rate * 100).toFixed(1)}%
              </p>
            </div>
            <div
              className="bg-white p-4 rounded-lg shadow-sm border cursor-pointer hover:border-blue-300 transition-colors"
              onClick={filterSmsOnly}
              data-testid="stat-sms-email"
            >
              <p className="text-sm text-gray-500">SMS / Email</p>
              <p className="text-2xl font-bold">
                {stats.by_channel?.sms || 0} / {stats.by_channel?.email || 0}
              </p>
            </div>
            <div
              className="bg-white p-4 rounded-lg shadow-sm border cursor-pointer hover:border-indigo-300 transition-colors"
              onClick={filterPushOnly}
              data-testid="stat-push"
            >
              <p className="text-sm text-gray-500 flex items-center gap-1.5">
                <Bell className="w-3.5 h-3.5" />
                Push
              </p>
              <p className="text-2xl font-bold text-indigo-600">
                {stats.by_channel?.push || 0}
              </p>
            </div>
          </div>
        )}
        {stats && (
          <p className="-mt-3 mb-6 text-xs text-gray-500" data-testid="metric-definitions">
            Delivered counts successful backend handoffs for SMS, email, in-app, and push.
            APNs acceptance does not prove a visible device notification. Delivery Rate is
            Delivered / (Delivered + Failed); skipped rows are excluded.
          </p>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm mb-6 border">
          <div
            className="p-4 flex items-center justify-between cursor-pointer"
            onClick={() => setShowFilters(!showFilters)}
            data-testid="filter-toggle"
          >
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-500" />
              <span className="font-medium">Filters</span>
              {Object.values(filters).some(v => v) && (
                <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                  Active
                </span>
              )}
            </div>
            {showFilters ? <ChevronUp /> : <ChevronDown />}
          </div>
          
          {showFilters && (
            <div className="p-4 border-t grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {/* Channel */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Channel
                </label>
                <select
                  value={filters.channel}
                  onChange={(e) => handleFilterChange('channel', e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  data-testid="filter-channel"
                >
                  <option value="">All Channels</option>
                  <option value="sms">SMS</option>
                  <option value="email">Email</option>
                  <option value="in_app">In-App</option>
                  <option value="push">Push</option>
                </select>
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  data-testid="filter-status"
                >
                  <option value="">All Statuses</option>
                  <option value="delivered">Delivered</option>
                  <option value="sent">Sent</option>
                  <option value="provider_accepted">Provider accepted</option>
                  <option value="queued">Queued</option>
                  <option value="pending">Pending</option>
                  <option value="skipped">Skipped</option>
                  <option value="failed">Failed</option>
                  <option value="undelivered">Undelivered</option>
                </select>
              </div>

              {/* Event Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Event Type
                </label>
                <select
                  value={filters.event_type}
                  onChange={(e) => handleFilterChange('event_type', e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  data-testid="filter-event-type"
                >
                  <option value="">All Events</option>
                  {eventTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* Recipient Search */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recipient
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={filters.recipient}
                    onChange={(e) => handleFilterChange('recipient', e.target.value)}
                    placeholder="Phone, email, or username"
                    className="w-full border rounded-lg pl-9 pr-3 py-2 text-sm"
                    data-testid="filter-recipient"
                  />
                </div>
              </div>

              {/* Game ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Game ID
                </label>
                <input
                  type="text"
                  value={filters.game_id}
                  onChange={(e) => handleFilterChange('game_id', e.target.value)}
                  placeholder="Enter game ID"
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  data-testid="filter-game-id"
                />
              </div>

              {/* Date Range */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="datetime-local"
                  value={filters.start_date}
                  onChange={(e) => handleFilterChange('start_date', e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  data-testid="filter-start-date"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="datetime-local"
                  value={filters.end_date}
                  onChange={(e) => handleFilterChange('end_date', e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  data-testid="filter-end-date"
                />
              </div>

              {/* Clear Filters */}
              <div className="flex items-end">
                <button
                  onClick={clearFilters}
                  className="text-gray-600 hover:text-gray-800 text-sm underline"
                  data-testid="clear-filters-button"
                >
                  Clear all filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Status Bar */}
        <div className="flex justify-between items-center mb-4">
          <p className="text-sm text-gray-500" data-testid="entry-count">
            Showing {entries.length} of {total} entries
          </p>
          <div className="flex items-center gap-3 text-sm text-gray-400">
            {autoRefresh && (
              <span className="flex items-center gap-1.5" data-testid="live-indicator">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                Live — every {refreshInterval}s
              </span>
            )}
            {lastRefreshed && (
              <span data-testid="last-refreshed">
                Last updated: {format(lastRefreshed, 'HH:mm:ss')}
              </span>
            )}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-4 border border-red-200" data-testid="error-banner">
            {error}
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden border">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="audit-table">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Timestamp
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Event
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Recipient
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Channel
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Delivery Info
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {entries.map((entry) => (
                  <tr key={entry.id} className={`hover:bg-gray-50 ${entry.channel === 'sms' ? 'bg-blue-50/30' : ''}`} data-testid={`audit-row-${entry.id}`}>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                      {entry.timestamp && formatLocalTime(entry.timestamp)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900">
                        {entry.event_type?.replace(/_/g, ' ')}
                      </div>
                      {entry.game_title && (
                        <div className="text-xs text-gray-500">
                          {entry.game_title}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-900">
                        {entry.recipient_username || 'Unknown'}
                      </div>
                      <div className="text-xs text-gray-500">
                        {entry.safe_mode_routed ? (
                          <span className="text-amber-600">
                            {entry.original_recipient} &rarr; {entry.actual_recipient}
                          </span>
                        ) : (
                          entry.recipient_contact
                        )}
                      </div>
                      {entry.safe_mode_routed && (
                        <span className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded mt-1">
                          <ShieldAlert className="w-3 h-3" />
                          SAFE
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <ChannelIcon channel={entry.channel} />
                        <span className="text-sm capitalize">{entry.channel?.replace('_', '-')}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <StatusIcon status={entry.status} />
                        <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${STATUS_STYLES[entry.status] || 'bg-gray-100'}`} data-testid={`status-badge-${entry.id}`}>
                          {entry.status}
                        </span>
                      </div>
                      {entry.provider_status && entry.provider_status !== entry.status && (
                        <div className="text-xs text-gray-400 mt-0.5 ml-6">
                          carrier: {entry.provider_status}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {/* SMS-specific delivery details inline */}
                      {entry.channel === 'sms' && (
                        <div className="text-xs space-y-0.5">
                          {entry.provider_message_id && (
                            <div className="font-mono text-gray-400" title={entry.provider_message_id} data-testid={`twilio-sid-${entry.id}`}>
                              <Zap className="w-3 h-3 inline text-blue-400 mr-1" />
                              {entry.provider_message_id.slice(0, 16)}...
                            </div>
                          )}
                          {entry.delivered_at && (
                            <div className="text-green-600" data-testid={`delivered-at-${entry.id}`}>
                              Delivered: {formatLocalTime(entry.delivered_at)}
                            </div>
                          )}
                          {entry.sent_at && !entry.delivered_at && (
                            <div className="text-blue-600">
                              Sent: {formatLocalTime(entry.sent_at)}
                            </div>
                          )}
                          {entry.failed_at && (
                            <div className="text-red-600" data-testid={`failed-at-${entry.id}`}>
                              Failed: {formatLocalTime(entry.failed_at)}
                            </div>
                          )}
                          {entry.provider_error_message && (
                            <div className="text-red-500 max-w-xs truncate" title={entry.provider_error_message} data-testid={`error-msg-${entry.id}`}>
                              {entry.provider_error_code && `[${entry.provider_error_code}] `}{entry.provider_error_message}
                            </div>
                          )}
                          {!entry.provider_message_id && !entry.provider_error_message && (
                            <div className="text-gray-400 italic">Awaiting status...</div>
                          )}
                        </div>
                      )}
                      {/* Email delivery details */}
                      {entry.channel === 'email' && (
                        <div className="text-xs space-y-0.5">
                          {entry.provider_message_id && (
                            <div className="font-mono text-gray-400" title={entry.provider_message_id}>
                              {entry.provider_message_id.slice(0, 16)}...
                            </div>
                          )}
                          {entry.delivered_at && (
                            <div className="text-green-600">
                              Delivered: {formatLocalTime(entry.delivered_at)}
                            </div>
                          )}
                          {entry.blocked_reason && (
                            <div className="text-red-500 max-w-xs truncate">
                              {entry.blocked_reason}
                            </div>
                          )}
                        </div>
                      )}
                      {entry.channel === 'push' && (
                        <div className="text-xs space-y-0.5" data-testid={`push-stage-${entry.id}`}>
                          <div className="font-medium text-indigo-700">
                            {getPushOutcomeLabel(entry)}
                          </div>
                          {entry.push_audit?.failure_reason && (
                            <div className="text-red-600 max-w-xs truncate">
                              {getReadablePushReason(entry.push_audit.failure_reason)}
                            </div>
                          )}
                        </div>
                      )}
                      {entry.channel === 'in_app' && (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => fetchDetail(entry.id)}
                        className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                        data-testid={`view-detail-${entry.id}`}
                      >
                        <Eye className="w-4 h-4" />
                        View
                      </button>
                    </td>
                  </tr>
                ))}
                {entries.length === 0 && !loading && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                      No notifications found matching your filters
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {total > pageSize && (
            <div className="px-4 py-3 border-t flex items-center justify-between">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4 py-2 text-sm border rounded-lg disabled:opacity-50"
                data-testid="pagination-prev"
              >
                Previous
              </button>
              <span className="text-sm text-gray-500">
                Page {page} of {Math.ceil(total / pageSize)}
              </span>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={page * pageSize >= total}
                className="px-4 py-2 text-sm border rounded-lg disabled:opacity-50"
                data-testid="pagination-next"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {selectedDelivery && (
        <MessageDetailModal
          delivery={selectedDelivery}
          onClose={() => setSelectedDelivery(null)}
        />
      )}
    </div>
  );
}
